import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import DoshaAssessmentPage from './pages/DoshaAssessmentPage';
import DoctorsPage from './pages/DoctorsPage';
import ProductsPage from './pages/ProductsPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import DoctorDashboard from './pages/DoctorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TreatmentsPage from './pages/TreatmentsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import CoursesPage from './pages/CoursesPage';
import AIAssistantPage from './pages/AIAssistantPage';
import CorporateSignupPage from './pages/CorporateSignupPage';
import CorporateDashboard from './pages/CorporateDashboard';
import CenterDashboard from './pages/CenterDashboard';
import YogaClassesPage from './pages/YogaClassesPage';
import BlogsPage from './pages/BlogsPage';
import BlogDetailPage from './pages/BlogDetailPage';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const roleDashboard = { admin: '/admin/dashboard', doctor: '/doctor/dashboard', corporate: '/corporate/dashboard', wellness_center: '/center/dashboard' };
    const to = roleDashboard[user.role] || '/dashboard';
    return <Navigate to={to} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/corporate/signup" element={<CorporateSignupPage />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute allowedRoles={['user']}>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/dosha-assessment"
        element={
          <PrivateRoute allowedRoles={['user']}>
            <DoshaAssessmentPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/doctors"
        element={
          <PrivateRoute>
            <DoctorsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/products"
        element={
          <PrivateRoute>
            <ProductsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/cart"
        element={
          <PrivateRoute>
            <CartPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <PrivateRoute>
            <OrdersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/treatments"
        element={
          <PrivateRoute>
            <TreatmentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <PrivateRoute>
            <SubscriptionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <PrivateRoute>
            <CoursesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <PrivateRoute>
            <AIAssistantPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/yoga"
        element={
          <PrivateRoute>
            <YogaClassesPage />
          </PrivateRoute>
        }
      />
      <Route path="/blogs" element={<BlogsPage />} />
      <Route path="/blogs/:blogId" element={<BlogDetailPage />} />
      <Route
        path="/doctor/dashboard"
        element={
          <PrivateRoute allowedRoles={['doctor']}>
            <DoctorDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/corporate/dashboard"
        element={
          <PrivateRoute allowedRoles={['corporate']}>
            <CorporateDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/center/dashboard"
        element={
          <PrivateRoute allowedRoles={['wellness_center']}>
            <CenterDashboard />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;